import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import {
  PatientPortalService,
  PortalSelectedSlotPayload,
} from '../../_services/patient-portal/patient-portal.service';

interface PortalTreatment {
  id: number;
  name: string;
  description?: string;
  duration_minutes: number;
  price: number;
}

interface AvailableSlot {
  calendars_id: number;
  start_utc: string;
  end_utc: string;
  start_local: string;
  end_local: string;
  timezone?: string;
  professionals_count?: number;
}

interface AvailableProfessional {
  users_id: number;
  calendars_id: number;
  name: string;
  surname: string;
  picture?: string | null;
  calendar?: { id: number; name: string };
}

interface SelectedSlot {
  treatment_id: number;
  calendars_id: number;
  users_id?: number | null;
  start_utc: string;
  end_utc: string;
  start_local: string;
  end_local: string;
  timezone?: string;
  professional?: AvailableProfessional | null;
}

interface CalendarCell {
  key: string;
  label: string;
  currentMonth: boolean;
  available: boolean;
  selected: boolean;
}

@Component({
  selector: 'app-public-booking',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './public-booking.component.html',
  styleUrl: './public-booking.component.scss',
})
export class PublicBookingComponent implements OnInit {
  customerIdentifier = '';
  siteIdentifier = '';

  loading = true;
  submitting = false;
  portalDisabled = false;
  submitErrorKey = '';
  successReference = '';
  successManageUrl = '';

  bootstrap: any = null;
  treatments: PortalTreatment[] = [];
  selectedTreatmentIds: number[] = [];
  selectedSlots: SelectedSlot[] = [];

  step: 'selection' | 'schedule' | 'review' | 'done' = 'selection';
  scheduleIndex = 0;

  patient = {
    name: '',
    surname: '',
    email: '',
    phone: '',
  };

  paymentMethod = '';
  agreeToPay = false;
  selectionValidationAttempted = false;

  currentMonthKey = '';
  monthAvailabilityDays = new Map<string, number>();
  selectedDayKey = '';
  availableSlots: AvailableSlot[] = [];
  selectedScheduleSlot: AvailableSlot | null = null;
  availableProfessionals: AvailableProfessional[] = [];
  selectedProfessionalUsersId: number | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly portalService: PatientPortalService
  ) {}

  ngOnInit(): void {
    this.customerIdentifier = `${this.route.snapshot.paramMap.get('customerIdentifier') || ''}`;
    this.siteIdentifier = `${this.route.snapshot.paramMap.get('siteIdentifier') || ''}`;
    this.loadPatientDraft();
    this.loadBootstrap();
  }

  get selectedTreatments(): PortalTreatment[] {
    const selected = new Set(this.selectedTreatmentIds.map((id) => Number(id)));
    return this.treatments.filter((treatment) => selected.has(Number(treatment.id)));
  }

  get currentTreatment(): PortalTreatment | undefined {
    return this.selectedTreatments[this.scheduleIndex];
  }

  get canContinueToSchedule(): boolean {
    return (
      !!`${this.patient.name || ''}`.trim() &&
      !!`${this.patient.surname || ''}`.trim() &&
      !!`${this.patient.email || ''}`.trim() &&
      !!`${this.patient.phone || ''}`.trim() &&
      this.selectedTreatmentIds.length > 0
    );
  }

  get usesProfessionalRouting(): boolean {
    return this.bootstrap?.bookingSettings?.routing_mode === 'BY_PROFESSIONAL';
  }

  get availablePaymentMethods(): Array<{ name: string; display_name?: string }> {
    const allowed = this.bootstrap?.bookingSettings?.allowed_payment_methods;
    if (!Array.isArray(allowed)) return [];
    return allowed
      .map((item: any) => ({
        name: `${item?.name || ''}`.trim(),
        display_name: `${item?.display_name || item?.name || ''}`.trim(),
      }))
      .filter((item) => !!item.name);
  }

  get hasAvailablePaymentMethods(): boolean {
    return this.availablePaymentMethods.length > 0;
  }

  get shouldShowPayment(): boolean {
    const policy = this.bootstrap?.bookingSettings?.booking_policy;
    return (
      policy === 'AUTO_IF_PAY_ELSE_APPROVAL' ||
      policy === 'REQUIRE_PAYMENT_ALWAYS'
    );
  }

  get monthLabel(): string {
    const [year, month] = this.currentMonthKey.split('-').map(Number);
    if (!year || !month) return '';
    return new Intl.DateTimeFormat('es-ES', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(year, month - 1, 1));
  }

  get selectedDayLabel(): string {
    const date = this.parseDate(this.selectedDayKey);
    if (!date) return this.selectedDayKey;

    const formatted = new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  get canGoToPreviousMonth(): boolean {
    const months = this.visibleMonthKeys();
    return months.indexOf(this.currentMonthKey) > 0;
  }

  get canGoToNextMonth(): boolean {
    const months = this.visibleMonthKeys();
    const index = months.indexOf(this.currentMonthKey);
    return index >= 0 && index < months.length - 1;
  }

  get calendarCells(): CalendarCell[] {
    const [year, month] = this.currentMonthKey.split('-').map(Number);
    if (!year || !month) return [];

    const firstOfMonth = new Date(year, month - 1, 1);
    const startWeekday = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells: CalendarCell[] = [];

    for (let index = 0; index < startWeekday; index += 1) {
      cells.push({
        key: `blank-${index}`,
        label: '',
        currentMonth: false,
        available: false,
        selected: false,
      });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const key = `${this.currentMonthKey}-${`${day}`.padStart(2, '0')}`;
      const available = this.monthAvailabilityDays.has(key);
      cells.push({
        key,
        label: `${day}`,
        currentMonth: true,
        available,
        selected: key === this.selectedDayKey,
      });
    }

    return cells;
  }

  private patientStorageKey(): string {
    return `public-booking:${this.customerIdentifier}:${this.siteIdentifier}:patient`;
  }

  private loadPatientDraft(): void {
    try {
      const raw = localStorage.getItem(this.patientStorageKey());
      if (!raw) return;
      const parsed = JSON.parse(raw);
      this.patient = {
        name: `${parsed?.name || ''}`.trim(),
        surname: `${parsed?.surname || ''}`.trim(),
        email: `${parsed?.email || ''}`.trim(),
        phone: `${parsed?.phone || ''}`.trim(),
      };
    } catch {
      // Ignore broken local drafts.
    }
  }

  private persistPatientDraft(): void {
    localStorage.setItem(this.patientStorageKey(), JSON.stringify(this.patient));
  }

  private loadBootstrap(): void {
    this.loading = true;
    this.portalService
      .bootstrap(this.customerIdentifier, this.siteIdentifier)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: any) => {
          this.bootstrap = response;
          this.portalDisabled =
            !response?.bookingSettings?.enabled ||
            !response?.bookingSettings?.module_enabled;

          if (this.portalDisabled) {
            return;
          }

          this.currentMonthKey = this.visibleMonthKeys()[0] || this.formatMonthKey(new Date());
          this.loadTreatments();
        },
        error: (error: HttpErrorResponse) => {
          const backendCode = this.resolveBackendErrorCode(error);
          if (
            error.status === 404 &&
            (backendCode === 'BOOKING_CUSTOMER_NOT_FOUND' ||
              backendCode === 'BOOKING_SITE_NOT_FOUND')
          ) {
            this.router.navigate(['/404']);
            return;
          }
          this.portalDisabled = true;
        },
      });
  }

  private resolveBackendErrorCode(error: HttpErrorResponse): string {
    const raw = Array.isArray(error?.error?.message)
      ? `${error?.error?.message?.[0] || ''}`
      : `${error?.error?.message || error?.error?.error || ''}`;
    return raw.trim().toUpperCase();
  }

  private loadTreatments(): void {
    this.loading = true;
    this.portalService
      .treatments(this.customerIdentifier, this.siteIdentifier)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (rows: any) => {
          this.treatments = Array.isArray(rows) ? rows : [];
        },
        error: () => {
          this.treatments = [];
        },
      });
  }

  toggleTreatment(treatment: PortalTreatment): void {
    const treatmentId = Number(treatment.id);
    if (this.selectedTreatmentIds.includes(treatmentId)) {
      this.selectedTreatmentIds = this.selectedTreatmentIds.filter(
        (id) => Number(id) !== treatmentId
      );
      this.selectedSlots = this.selectedSlots.filter(
        (slot) => Number(slot.treatment_id) !== treatmentId
      );
      return;
    }

    this.selectedTreatmentIds = [...this.selectedTreatmentIds, treatmentId];
  }

  continueToSchedule(): void {
    this.selectionValidationAttempted = true;
    this.submitErrorKey = '';

    if (!this.canContinueToSchedule) {
      this.submitErrorKey = 'Completa tus datos y elige al menos un tratamiento.';
      return;
    }

    this.persistPatientDraft();
    this.step = 'schedule';
    this.scheduleIndex = 0;
    this.prepareScheduleStep();
  }

  private prepareScheduleStep(): void {
    const currentSelection = this.selectedSlots.find(
      (slot) => Number(slot.treatment_id) === Number(this.currentTreatment?.id || 0)
    );

    this.selectedScheduleSlot = currentSelection || null;
    this.selectedProfessionalUsersId = currentSelection?.users_id || null;
    this.selectedDayKey = currentSelection
      ? this.extractDayKey(currentSelection.start_local, currentSelection.start_utc)
      : '';
    this.currentMonthKey = this.selectedDayKey
      ? this.selectedDayKey.slice(0, 7)
      : this.visibleMonthKeys()[0] || this.formatMonthKey(new Date());
    this.availableProfessionals = [];
    this.loadMonthAvailability();
  }

  private getSelectedSlotsPayload(excludeTreatmentId?: number): PortalSelectedSlotPayload[] {
    return this.selectedSlots
      .filter((slot) =>
        excludeTreatmentId ? Number(slot.treatment_id) !== Number(excludeTreatmentId) : true
      )
      .map((slot) => ({
        calendars_id: slot.calendars_id,
        users_id: slot.users_id ?? null,
        start_utc: slot.start_utc,
        end_utc: slot.end_utc,
      }));
  }

  private loadMonthAvailability(): void {
    if (!this.currentTreatment) return;

    this.loading = true;
    this.monthAvailabilityDays.clear();
    this.availableSlots = [];
    this.availableProfessionals = [];

    this.portalService
      .availabilityMonth(this.customerIdentifier, this.siteIdentifier, {
        treatment_id: this.currentTreatment.id,
        month: this.currentMonthKey,
        selected_slots: this.getSelectedSlotsPayload(this.currentTreatment.id),
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: any) => {
          for (const row of response?.days || []) {
            const key = `${row?.day || ''}`.trim();
            const count = Number(row?.slots_count || 0);
            if (key && count > 0) {
              this.monthAvailabilityDays.set(key, count);
            }
          }

          if (this.selectedDayKey && this.monthAvailabilityDays.has(this.selectedDayKey)) {
            this.loadSlotsForDay(this.selectedDayKey);
            return;
          }

          const firstAvailableDay = Array.from(this.monthAvailabilityDays.keys())[0] || '';
          this.selectedDayKey = '';
          if (firstAvailableDay) {
            this.loadSlotsForDay(firstAvailableDay);
          }
        },
        error: () => {
          this.monthAvailabilityDays.clear();
        },
      });
  }

  previousMonth(): void {
    const months = this.visibleMonthKeys();
    const index = months.indexOf(this.currentMonthKey);
    if (index > 0) {
      this.currentMonthKey = months[index - 1];
      this.selectedDayKey = '';
      this.selectedScheduleSlot = null;
      this.selectedProfessionalUsersId = null;
      this.loadMonthAvailability();
    }
  }

  nextMonth(): void {
    const months = this.visibleMonthKeys();
    const index = months.indexOf(this.currentMonthKey);
    if (index >= 0 && index < months.length - 1) {
      this.currentMonthKey = months[index + 1];
      this.selectedDayKey = '';
      this.selectedScheduleSlot = null;
      this.selectedProfessionalUsersId = null;
      this.loadMonthAvailability();
    }
  }

  selectCalendarDay(dayKey: string): void {
    if (!this.monthAvailabilityDays.has(dayKey)) return;
    this.loadSlotsForDay(dayKey);
  }

  private loadSlotsForDay(dayKey: string): void {
    if (!this.currentTreatment) return;

    this.loading = true;
    this.selectedDayKey = dayKey;
    this.selectedScheduleSlot = null;
    this.availableProfessionals = [];
    this.selectedProfessionalUsersId = null;

    this.portalService
      .availability(this.customerIdentifier, this.siteIdentifier, {
        treatment_id: this.currentTreatment.id,
        day: dayKey,
        selected_slots: this.getSelectedSlotsPayload(this.currentTreatment.id),
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: any) => {
          this.availableSlots = Array.isArray(response?.slots) ? response.slots : [];
        },
        error: () => {
          this.availableSlots = [];
        },
      });
  }

  selectSlot(slot: AvailableSlot): void {
    this.selectedScheduleSlot = slot;
    this.selectedProfessionalUsersId = null;
    this.availableProfessionals = [];

    if (!this.usesProfessionalRouting || !this.currentTreatment || !this.selectedDayKey) {
      return;
    }

    this.loading = true;
    this.portalService
      .availabilityProfessionals(this.customerIdentifier, this.siteIdentifier, {
        treatment_id: this.currentTreatment.id,
        day: this.selectedDayKey,
        start_utc: slot.start_utc,
        end_utc: slot.end_utc,
        selected_slots: this.getSelectedSlotsPayload(this.currentTreatment.id),
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: any) => {
          this.availableProfessionals = Array.isArray(response?.professionals)
            ? response.professionals
            : [];
        },
        error: () => {
          this.availableProfessionals = [];
        },
      });
  }

  selectProfessional(usersId: number | null): void {
    this.selectedProfessionalUsersId = usersId;
  }

  confirmSelectedSlot(): void {
    if (!this.currentTreatment || !this.selectedScheduleSlot) {
      this.submitErrorKey = 'Selecciona primero una hora disponible.';
      return;
    }

    let professional: AvailableProfessional | null = null;
    let calendarsId = this.selectedScheduleSlot.calendars_id;
    let usersId: number | null = null;

    if (this.usesProfessionalRouting) {
      if (this.selectedProfessionalUsersId === null && this.availableProfessionals.length > 0) {
        this.submitErrorKey = 'Selecciona un profesional o deja la asignación automática.';
        return;
      }

      professional =
        this.availableProfessionals.find(
          (item) => Number(item.users_id) === Number(this.selectedProfessionalUsersId)
        ) || null;
      calendarsId = professional?.calendars_id || this.selectedScheduleSlot.calendars_id;
      usersId = professional?.users_id || null;
    }

    const nextSelection: SelectedSlot = {
      treatment_id: this.currentTreatment.id,
      calendars_id: calendarsId,
      users_id: usersId,
      start_utc: this.selectedScheduleSlot.start_utc,
      end_utc: this.selectedScheduleSlot.end_utc,
      start_local: this.selectedScheduleSlot.start_local,
      end_local: this.selectedScheduleSlot.end_local,
      timezone: this.selectedScheduleSlot.timezone,
      professional,
    };

    this.selectedSlots = [
      ...this.selectedSlots.filter(
        (slot) => Number(slot.treatment_id) !== Number(this.currentTreatment?.id || 0)
      ),
      nextSelection,
    ];

    this.submitErrorKey = '';

    if (this.scheduleIndex < this.selectedTreatments.length - 1) {
      this.scheduleIndex += 1;
      this.prepareScheduleStep();
      return;
    }

    this.step = 'review';
  }

  goBackToSelection(): void {
    this.step = 'selection';
    this.submitErrorKey = '';
  }

  goBackToSchedule(): void {
    this.step = 'schedule';
    this.scheduleIndex = Math.max(this.selectedTreatments.length - 1, 0);
    this.prepareScheduleStep();
  }

  submitRequests(): void {
    this.submitting = true;
    this.submitErrorKey = '';

    const payload = {
      patient: {
        ...this.patient,
      },
      selections: this.selectedSlots.map((slot) => ({
        treatment_id: slot.treatment_id,
        calendars_id: slot.calendars_id,
        users_id: slot.users_id ?? null,
        start_utc: slot.start_utc,
        end_utc: slot.end_utc,
      })),
      payment_method: this.paymentMethod || undefined,
      agree_to_pay: this.agreeToPay,
    };

    this.portalService
      .createRequests(this.customerIdentifier, this.siteIdentifier, payload)
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (response: any) => {
          this.successReference = `${response?.booking_reference || ''}`.trim();
          this.successManageUrl = `${response?.manage_url || ''}`.trim();
          this.step = 'done';
        },
        error: (error: HttpErrorResponse) => {
          this.submitErrorKey = this.resolveSubmitError(error);
        },
      });
  }

  private resolveSubmitError(error: HttpErrorResponse): string {
    const backendError = `${error?.error?.message || error?.error?.err || ''}`.trim();
    if (backendError) return backendError;
    return 'No hemos podido completar la reserva. Inténtalo de nuevo.';
  }

  getSummaryRows(): Array<{ treatment: PortalTreatment; slot: SelectedSlot | undefined }> {
    return this.selectedTreatments.map((treatment) => ({
      treatment,
      slot: this.selectedSlots.find(
        (slot) => Number(slot.treatment_id) === Number(treatment.id)
      ),
    }));
  }

  formatPrice(value: number): string {
    const amount = Number(value || 0);
    const currency = `${this.bootstrap?.customer?.currency?.code || this.bootstrap?.site?.currency || 'EUR'}`.trim();

    try {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  formatTimeRange(slot?: {
    start_local?: string;
    end_local?: string;
    start_utc?: string;
    end_utc?: string;
  }): string {
    const start = this.parseDate(slot?.start_local || slot?.start_utc);
    const end = this.parseDate(slot?.end_local || slot?.end_utc);
    if (!start || !end) return '';

    const timeFormatter = new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${timeFormatter.format(start)} - ${timeFormatter.format(end)}`;
  }

  getSlotAvailabilityLabel(slot: AvailableSlot): string {
    if (!this.usesProfessionalRouting) return '';
    const count = Number(slot.professionals_count || 0);
    if (count <= 0) return '';
    if (count === 1) return '1 profesional disponible';
    return `${count} profesionales disponibles`;
  }

  getProfessionalName(professional?: AvailableProfessional | null): string {
    return `${professional?.name || ''} ${professional?.surname || ''}`.trim();
  }

  visibleMonthKeys(): string[] {
    const totalDays = Math.min(
      Number(this.bootstrap?.bookingSettings?.show_days_ahead || 14),
      90
    );
    const now = new Date();
    const months = new Set<string>();

    for (let index = 0; index < Math.max(totalDays, 1); index += 1) {
      const date = new Date(now);
      date.setDate(now.getDate() + index);
      months.add(this.formatMonthKey(date));
    }

    return Array.from(months.values());
  }

  private extractDayKey(startLocal?: string, startUtc?: string): string {
    const raw = `${startLocal || startUtc || ''}`;
    return raw.slice(0, 10);
  }

  private formatMonthKey(date: Date): string {
    return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}`;
  }

  private parseDate(value?: string): Date | null {
    const raw = `${value || ''}`.trim();
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
}
